import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AlertModel } from '../models/alert.model';

/**
 * This service manages Alerts in the camera application database
 */
@Injectable({
  providedIn: 'root',
})
export class AlertService {
  // Endpoints
  private readonly alertEndPoint: string = environment.managementApi + '/Alerts/';

  // Variables
  alerts: AlertModel[];

  constructor(private http: HttpClient) { }

  /**
   * Get all alerts from the management API
   * @returns Promise
   */
  async getAllAlerts(): Promise<AlertModel[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get<AlertModel[]>(`${environment.managementApi}/alerts/all`)
        .subscribe({
          next: (rtnval) => resolve(rtnval),
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Update an existing alert
   * @param  {AlertModel} alert - Alert model to update
   * @returns Promise
   */
  async updateAlert(alert: AlertModel): Promise<AlertModel> {
    return new Promise((resolve, reject) => {
      this.http
        .put<AlertModel>(`${environment.managementApi}/Alerts/update`, alert)
        .subscribe({
          next: (rtnval) => {
            let index = this.alerts.findIndex((a) => a.Id == rtnval.Id);
            this.alerts[index] = rtnval;
            resolve(rtnval);
          },
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Add a new alert
   * @param  {AlertModel} alert - Alert model to add
   * @returns Promise
   */
  async addAlert(alert: AlertModel): Promise<AlertModel> {
    return new Promise((resolve, reject) => {
      this.http
        .post<AlertModel>(`${environment.managementApi}/Alerts/add`, alert)
        .subscribe({
          next: (rtnval) => {
            this.alerts.push(rtnval);
            resolve(rtnval);
          },
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Delete an alert by ID
   * @param  {number} alertId - Alert ID
   * @returns Promise
   */
  async deleteAlert(alertId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .delete(`${environment.managementApi}/Alerts/delete/${alertId}`)
        .subscribe({
          next: () => {
            this.alerts = this.alerts.filter((a) => a.Id != alertId);
            resolve();
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  /**
   * Check if an alert name is available
   * @param  {string} alertName - Alert name to check
   * @param  {number} alertId - Optional alert ID (for update operations)
   * @returns Promise
   */
  async checkAlertName(
    alertName: string,
    alertId: number = 0
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let test = {
        Name: alertName,
        id: alertId,
      };

      this.http
        .post<boolean>(`${environment.managementApi}/Alerts/CheckName`, test)
        .subscribe({
          next: (rtnval) => resolve(rtnval),
          error: (err) => reject(err)
        });
    });
  }

  /**
   * Enable or disable a specific alert.
   * @param  {AlertModel} alert - The Alert Object.
   * @returns Promise<AlertModel> - Return the updated alert object.
   */
  async toggleAlert(alert: AlertModel): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .put<AlertModel>(`${this.alertEndPoint}${alert.Id}/setEnabled?enabled=${alert.Enabled}`, null)
        .subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });

    });
  }
}
